import React, { useEffect, useState } from "react";
import { fetchTables, fetchVisitQR, getUser, hasFeature } from "../services/api";
import { useT } from "../i18n/useT";
import { escapeHtml, safeUrl } from "../utils/escapeHtml";

// AM/OM are machine drafts (2026-06) — pending native review
const STRINGS = {
  en: {
    heading: "QR Codes",
    could_not_load: "Could not load tables.",
    your_restaurant_id: "Your Restaurant ID:",
    customers_hint_a: "Customers can scan any table QR below, or visit the customer app and enter",
    restaurant_id_eq: "Restaurant ID = {n}",
    plus: "+",
    table_number_label: "Table number",
    print_area: "Print this area",
    unassigned: "Unassigned",
    manually: "manually.",
    table_label: "Table {n}",
    qr_alt: "Table {n} QR",
    no_qr_generated: "No QR generated",
    download: "Download",
    no_tables_yet: "No tables yet.",
    visit_qr_title: "Rate-your-visit QR",
    visit_qr_hint: "Print this for the table tent or exit. Any guest can rate \u2014 even walk-ins or waiter-served guests who didn\u2019t order in the app.",
    sec_dining: "Dining outlets",
    sec_room: "Room service",
  },
  am: {
    heading: "QR ኮዶች",
    could_not_load: "ጠረጴዛዎችን መጫን አልተቻለም።",
    your_restaurant_id: "የእርስዎ ሬስቶራንት መታወቂያ፦",
    customers_hint_a: "ደንበኞች ከታች ያለውን ማንኛውንም የጠረጴዛ QR መቃኘት ይችላሉ፣ ወይም የደንበኛ መተግበሪያውን ጎብኝተው ያስገቡ",
    restaurant_id_eq: "የሬስቶራንት መታወቂያ = {n}",
    plus: "+",
    table_number_label: "የጠረጴዛ ቁጥር",
    manually: "በእጅ።",
    table_label: "ጠረጴዛ {n}",
    qr_alt: "የጠረጴዛ {n} QR",
    no_qr_generated: "QR አልተፈጠረም",
    download: "አውርድ",
    no_tables_yet: "እስካሁን ጠረጴዛ የለም።",
    visit_qr_title: "ጉብኝትዎን ይገምግሙ QR",
    visit_qr_hint: "ይህን ለጠረጴዛ ወይም ለመውጫ ያትሙ። ማንኛውም እንግዳ መገምገም ይችላል \u2014 በመተግበሪያው ያላዘዙትም ቢሆኑ።",
    sec_dining: "የመመገቢያ ስፍራዎች",
    sec_room: "የክፍል አገልግሎት",
  },
  om: {
    heading: "Koodota QR",
    could_not_load: "Minjaalota fe'uun hin danda'amne.",
    your_restaurant_id: "ID Mana Nyaataa Keessanii:",
    customers_hint_a: "Maamiltoonni koodii QR minjaala kamiyyuu gadii kana scan gochuu, yookaan appii maamilaa daawwatanii galchuu danda'u",
    restaurant_id_eq: "ID Mana Nyaataa = {n}",
    plus: "+",
    table_number_label: "Lakkoofsa minjaala",
    manually: "harkaan.",
    table_label: "Minjaala {n}",
    qr_alt: "Minjaala {n} QR",
    no_qr_generated: "QR hin uumamne",
    download: "Buufadhu",
    no_tables_yet: "Minjaalli hin jiru.",
    visit_qr_title: "Daawwannaa madaaluu QR",
    visit_qr_hint: "Kana minjaala yookaan bahaaf maxxansi. Keessummaan kamiyyuu madaaluu danda\u2019a \u2014 kanneen appii keessatti hin ajajinis.",
    sec_dining: "Bakka nyaataa",
    sec_room: "Tajaajila kutaa",
  },
};

// QR Codes page — split out of ManagerTablesPage (2026-06-05) so table
// management and QR printing live under separate nav items
// ("Table & QR Setup" group). The QR card grid + download logic moved
// here verbatim; table create/delete stays on ManagerTablesPage.
// Both pages duplicate the small fetchTables() call on purpose —
// simpler than inventing shared state for a read-only list.
export default function QRCodesPage() {
  const { lang } = useT();
  const t = (k) => (STRINGS[lang] && STRINGS[lang][k]) || STRINGS.en[k] || k;

  const [tables, setTables] = useState([]);
  const [error, setError] = useState(null);
  const [visitQR, setVisitQR] = useState(null);
  const ratingsOn = hasFeature("ratings");

  // The restaurant ID is the value the customer types into the landing
  // page helper or the value embedded in QR-code URLs. Surfacing it here
  // saves the manager from digging through Postgres or the Render logs.
  const restaurantId = getUser()?.id;

  const load = () =>
    fetchTables()
      .then(setTables)
      .catch((e) => setError(e?.response?.data?.detail || t("could_not_load")));

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!ratingsOn) return;
    fetchVisitQR().then(setVisitQR).catch(() => {});
  }, [ratingsOn]);

  const downloadVisit = () => {
    if (!visitQR?.qr_code_url) return;
    const a = document.createElement("a");
    a.href = visitQR.qr_code_url; a.download = "rate-your-visit-qr.png";
    document.body.appendChild(a); a.click(); a.remove();
  };
  const printVisit = () => {
    if (!visitQR) return;
    const w = window.open("", "_blank"); if (!w) return;
    w.document.write(
      `<html><head><title>${escapeHtml(t("visit_qr_title"))} — ODRA</title></head>` +
      `<body style="font-family:Arial;text-align:center"><h2>${escapeHtml(t("visit_qr_title"))}</h2>` +
      `<img src="${escapeHtml(safeUrl(visitQR.qr_code_url))}" style="width:260px;height:260px"/>` +
      `<div style="font-size:10px;color:#555;word-break:break-all">${escapeHtml(visitQR.scan_url || "")}</div>` +
      `<scr` + `ipt>window.onload=function(){window.print()}<\/scr` + `ipt></body></html>`
    );
    w.document.close();
  };

  const downloadQR = (table) => {
    if (!table.qr_code_url) return;
    const a = document.createElement("a");
    a.href = table.qr_code_url;
    a.download = `table-${table.table_number}-qr.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const printArea = (area, list) => {
    const w = window.open("", "_blank");
    if (!w) return;
    const cards = list.map((tb) =>
      `<div style="display:inline-block;text-align:center;margin:12px;width:200px;vertical-align:top">`
      + `<div style="font-weight:bold;margin-bottom:4px">${escapeHtml(t("table_label").replace("{n}", tb.table_number))}</div>`
      + (tb.qr_code_url ? `<img src="${escapeHtml(safeUrl(tb.qr_code_url))}" style="width:180px;height:180px"/>` : "")
      + `<div style="font-size:9px;word-break:break-all;color:#555">${escapeHtml(tb.scan_url || "")}</div></div>`
    ).join("");
    w.document.write(
      `<html><head><title>${escapeHtml(area)} — ODRA QR</title></head><body style="font-family:Arial"><h2>${escapeHtml(area)}</h2>${cards}` +
      `<scr` + `ipt>window.onload=function(){window.print()}<\/scr` + `ipt></body></html>`
    );
    w.document.close();
  };

  return (
    <>
      <h1>{t("heading")}</h1>

      {restaurantId != null && (
        <div
          className="card"
          style={{
            marginBottom: 16,
            padding: 14,
            background: "#fffaf0",
            borderColor: "#f0d38b",
          }}
        >
          <div style={{ fontSize: "0.85rem", color: "#6c5a00" }}>
            <b>{t("your_restaurant_id")}</b>{" "}
            <code style={{ fontSize: "1rem" }}>{restaurantId}</code>
            <span className="muted" style={{ marginLeft: 8 }}>
              {t("customers_hint_a")}{" "}
              <b>{t("restaurant_id_eq").replace("{n}", restaurantId)}</b> {t("plus")}{" "}
              <b>{t("table_number_label")}</b> {t("manually")}
            </span>
          </div>
        </div>
      )}

      {ratingsOn && visitQR && (
        <div className="card" style={{ marginBottom: 16, padding: 14 }}>
          <div className="space-between" style={{ marginBottom: 6 }}>
            <h2 style={{ margin: 0, fontSize: "1.1rem" }}>🙏 {t("visit_qr_title")}</h2>
            <button className="btn secondary small" onClick={printVisit}>{t("print_area")}</button>
          </div>
          <p className="muted" style={{ marginTop: 0, fontSize: "0.85rem" }}>{t("visit_qr_hint")}</p>
          <div style={{ display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
            <img src={visitQR.qr_code_url} alt={t("visit_qr_title")} style={{ width: 160, height: 160 }} />
            <div>
              <p className="muted" style={{ fontSize: "0.8rem", wordBreak: "break-all", maxWidth: 320 }}>{visitQR.scan_url}</p>
              <button className="btn secondary small" onClick={downloadVisit}>{t("download")}</button>
            </div>
          </div>
        </div>
      )}

      {error && <div className="error">{error}</div>}

      {tables.length === 0 && <p className="muted">{t("no_tables_yet")}</p>}
      {(() => {
        // Render the existing per-area sub-grouping for a subset of tables.
        const renderAreaGroups = (list) => {
          const groups = {};
          list.forEach((tb) => { const a = tb.area || t("unassigned"); (groups[a] = groups[a] || []).push(tb); });
          return Object.keys(groups).sort((a, b) => a.localeCompare(b)).map((a) => (
            <div key={a} style={{ marginBottom: 24 }}>
              <div className="space-between" style={{ marginBottom: 8 }}>
                <h2 style={{ margin: 0 }}>{a} <span className="muted" style={{ fontSize: "0.8rem" }}>({groups[a].length})</span></h2>
                <button className="btn secondary small" onClick={() => printArea(a, groups[a])}>{t("print_area")}</button>
              </div>
              <div className="grid-cards">
                {groups[a].map((tb) => (
                  <div key={tb.id} className="qr-card">
                    <h3>{t("table_label").replace("{n}", tb.table_number)}</h3>
                    {tb.qr_code_url ? (
                      <img src={tb.qr_code_url} alt={t("qr_alt").replace("{n}", tb.table_number)} />
                    ) : (
                      <p className="muted">{t("no_qr_generated")}</p>
                    )}
                    <p className="muted" style={{ fontSize: "0.8rem", wordBreak: "break-all" }}>{tb.scan_url}</p>
                    <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                      <button className="btn secondary small" onClick={() => downloadQR(tb)}>{t("download")}</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ));
        };

        // Split tables into dining outlets vs in-room room-service QR codes.
        const diningTables = tables.filter((tb) => (tb.kind || "dining") !== "room");
        const roomTables = tables.filter((tb) => (tb.kind || "dining") === "room");

        const topHeadingStyle = {
          margin: "0 0 12px",
          fontSize: "1.5rem",
          fontWeight: 700,
          borderBottom: "2px solid var(--color-navy, #002147)",
          paddingBottom: 6,
        };

        return (
          <>
            {diningTables.length > 0 && (
              <section style={{ marginBottom: 32 }}>
                <h2 style={topHeadingStyle}>{t("sec_dining")}</h2>
                {renderAreaGroups(diningTables)}
              </section>
            )}
            {roomTables.length > 0 && (
              <section style={{ marginBottom: 32 }}>
                <h2 style={topHeadingStyle}>{t("sec_room")}</h2>
                {renderAreaGroups(roomTables)}
              </section>
            )}
          </>
        );
      })()}
    </>
  );
}
